import {
    Box,
    CSSObject,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Theme,
    useTheme
} from "@mui/material"
import {
    AutoStoriesOutlined as AutoStoriesOutlinedIcon,
    BookOutlined as BookOutlinedIcon,
    CalendarMonthOutlined as CalendarMonthOutlinedIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    GavelOutlined as GavelOutlinedIcon,
    HomeOutlined as HomeOutlinedIcon,
    LoginOutlined as LoginOutlinedIcon,
    AccountCircleOutlined as AccountCircleOutlinedIcon,
    AdminPanelSettingsOutlined as AdminPanelSettingsOutlinedIcon,
    GroupOutlined as GroupOutlinedIcon,
    // MessageOutlined as MessageOutlinedIcon,
    // ForumOutlined as ForumOutlinedIcon,
    DashboardOutlined as DashboardOutlinedIcon,
} from "@mui/icons-material"
import { Link } from "react-router-dom"
import { useAppSelector } from "../../redux/hooks"
import { ReactNode } from "react"

interface SidebarProps {
    open: boolean
    setOpen: (x: boolean) => void
}

type NavItem = {
    title: string
    href: string
    icon: JSX.Element
}

export const Sidebar = ({ open, setOpen }: SidebarProps) => {

    const authPending = useAppSelector(state => state.auth.pending)
    const auth = useAppSelector(state => state.auth)
    const isAuthenticated = !!auth.data
    const theme = useTheme()
    const sidebarWidth = 240

    const isAdmin = auth.idTokenPayload?.["cognito:groups"]?.includes('admin') || auth.idTokenPayload?.["cognito:groups"]?.includes('superadmin')
    const isMember = auth.idTokenPayload?.["cognito:groups"]?.includes('member') || isAdmin

    const toggleDrawerOpen = () => {
        setOpen(!open)
    }

    const navItems: NavItem[] = [
        {
            title: 'Home',
            href: '/',
            icon: <HomeOutlinedIcon />
        },
        {
            title: 'Our History',
            href: '/history',
            icon: <AutoStoriesOutlinedIcon />
        },
    ]

    const unauthNavItems: NavItem[] = [
        {
            title: 'Sign In',
            href: '/auth/sign-in',
            icon: <LoginOutlinedIcon />
        }
    ]

    const authNavItems: NavItem[] = [
        // {
        //     title: 'Messages',
        //     href: '/messages',
        //     icon: <MessageOutlinedIcon />
        // },
        {
            title: 'My Account',
            href: '/my-account',
            icon: <AccountCircleOutlinedIcon />
        }
    ]

    const memberNavItems: NavItem[] = [
        {
            title: 'Dashboard',
            href: '/members/dashboard',
            icon: <DashboardOutlinedIcon />,
        },
        {
            title: 'Calendar',
            href: '/calendar',
            icon: <CalendarMonthOutlinedIcon />
        },
        {
            title: 'Rules',
            href: '/rules',
            icon: <BookOutlinedIcon />
        },
        {
            title: 'Bylaws',
            href: '/bylaws',
            icon: <GavelOutlinedIcon />
        },
        {
            title: 'Members',
            href: '/members',
            icon: <GroupOutlinedIcon />
        },
        // {
        //     title: 'Message Board',
        //     href: '/message-board',
        //     icon: <ForumOutlinedIcon />,
        // }
    ]

    const adminNavItems: NavItem[] = [
        {
            title: 'Administrator',
            href: '/admin',
            icon: <AdminPanelSettingsOutlinedIcon />
        }
    ]

    const openedMixin = (theme: Theme): CSSObject => ({
        width: sidebarWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        overflowX: 'hidden',
    });

    const closedMixin = (theme: Theme): CSSObject => ({
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        overflowX: 'hidden',
        width: `calc(${theme.spacing(7)} + 1px)`,
        [theme.breakpoints.up('sm')]: {
            width: `calc(${theme.spacing(8)} + 1px)`,
        },
    });

    const handleLinkClick = () => {
        setOpen(false)
    }

    return (
        <Box
            className="sidebar__wrapper"
            sx={{
                display: 'flex',
                position: 'fixed',
                top: 0,
                left: 0,
                zIndex: 100,
            }}
        >
            <Drawer variant="permanent" open={open} sx={{
                width: sidebarWidth,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                boxSizing: 'border-box',
                ...(open && {
                    ...openedMixin(theme),
                    '& .MuiDrawer-paper': openedMixin(theme),
                }),
                ...(!open && {
                    ...closedMixin(theme),
                    '& .MuiDrawer-paper': closedMixin(theme),
                }),
            }}>
                <Box
                    className="sidebar__header" sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        padding: theme.spacing(0, 1),
                        px: 1,
                        ...theme.mixins.toolbar,
                    }}
                >
                    <IconButton className="sidebar__toggle" onClick={toggleDrawerOpen}>
                        {!open ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                    </IconButton>
                </Box>
                <Divider />
                <List className="sidebar__links-container">
                    {navItems.map(x => <SidebarLink key={x.href} href={x.href} icon={x.icon} title={x.title} onClick={handleLinkClick} sidebarOpen={open} />)}
                </List>
                <Divider />
                {
                    !authPending && !isAuthenticated && (
                        <>
                            <List className="sidebar__links-container">
                                {unauthNavItems.map(x => <SidebarLink key={x.href} href={x.href} icon={x.icon} title={x.title} onClick={handleLinkClick} sidebarOpen={open} />)}
                            </List>
                            <Divider />
                        </>
                    )
                }
                {
                    !authPending && isAuthenticated && isMember && (
                        <>
                            <List className="sidebar__links-container">
                                {memberNavItems.map(x => <SidebarLink key={x.href} href={x.href} icon={x.icon} title={x.title} onClick={handleLinkClick} sidebarOpen={open} />)}
                            </List>
                            <Divider />
                        </>
                    )
                }
                {
                    !authPending && isAuthenticated && isAdmin && (
                        <>
                            <List className="sidebar__links-container">
                                {adminNavItems.map(x => <SidebarLink key={x.href} href={x.href} icon={x.icon} title={x.title} onClick={handleLinkClick} sidebarOpen={open} />)}
                            </List>
                            <Divider />
                        </>
                    )
                }
                {
                    !authPending && isAuthenticated && (
                        <>
                            <List className="sidebar__links-container">
                                {authNavItems.map(x => <SidebarLink key={x.href} href={x.href} icon={x.icon} title={x.title} onClick={handleLinkClick} sidebarOpen={open} />)}
                            </List>
                            <Divider />
                        </>
                    )
                }
            </Drawer>
        </Box>
    )
}

interface SidebarLinkProps {
    href: string
    title: string
    sidebarOpen: boolean
    icon: ReactNode
    onClick: () => void
}

const SidebarLink = ({ href, title, sidebarOpen, icon, onClick }: SidebarLinkProps) => {
    return (
        <Link to={href} key={title} onClick={onClick} className="sidebar__link-wrapper" style={{ textDecoration: 'none', color: '#000000' }}>
            <ListItem disablePadding sx={{ display: 'block', }}>
                <ListItemButton
                    sx={{
                        minHeight: 48,
                        justifyContent: sidebarOpen ? 'initial' : 'center',
                        px: 2.5,
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 0,
                            mr: sidebarOpen ? 3 : 'auto',
                            justifyContent: 'right',
                        }}
                    >
                        {icon}
                    </ListItemIcon>
                    <ListItemText
                        sx={{ opacity: sidebarOpen ? 1 : 0 }}
                    >{title}</ListItemText>
                </ListItemButton>
            </ListItem>
        </Link>
    )
}